/**
 * Text editor component for use in the Gotoko frontend.
 *
 * When ready, the editor will emit a setup-editor-hooks event.
 * This event should be captured by the parent component to set up the hooks
 * for loading and saving documents.
 */
import shoelaceDark from "@shoelace-style/shoelace/dist/themes/dark.css?inline";
import shoelaceLight from "@shoelace-style/shoelace/dist/themes/light.css?inline";

import { CSSResult, CSSResultGroup, LitElement, html, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@/wc/text-editor/component/outline";
import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import {
    BaseEditorConfig,
    configureEditor,
} from "@/wc/text-editor/lib/core/config";
import { ExtensionKeys, ExtensionMap } from "@/wc/text-editor/lib/extensions";
import { ProseMirrorViewController } from "@/wc/text-editor/lib/lit-prosemirror-controller";
import defaultStyles from "@/wc/text-editor/styles/default.scss?inline";
import overridesStyles from "@/wc/text-editor/styles/overrides.scss?inline";
import shoelaceStekzTheme from "@/wc/text-editor/styles/shoelace-stekz-theme.scss?inline";

@customElement("c-text-editor")
export class TextEditor extends LitElement {
    @property({
        attribute: "data-editor-extensions",
        type: Array<ExtensionKeys>,
        converter: {
            fromAttribute: (value) => {
                return value ? value.split(",").map((x) => x.trim()) : [];
            },
            toAttribute: (value: Array<ExtensionKeys>): string => {
                return value.join(",");
            },
        },
    })
    extensions: ExtensionKeys[] = [];

    @property({
        attribute: "data-reading-mode",
        type: Boolean,
    })
    readingMode = true;

    @property({
        attribute: "data-dark-mode",
        type: Boolean,
    })
    darkMode = false;

    @property({
        attribute: "data-hide-menu-bar",
        type: Boolean,
        // Reflected so the stylesheet can restyle the chrome-less variant
        // via :host([data-hide-menu-bar]).
        reflect: true,
    })
    hideMenuBar = false;

    @property({
        attribute: "data-placeholder",
    })
    placeholder = "";

    private pm: ProseMirrorViewController | undefined;
    private additionalStyles: CSSResult[] = [];

    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();

        this.configureController();
    }

    /**
     * Construct a list of extension objects.
     *
     * This function is async because modules may need to be imported
     * for external extensions.
     */
    async constructExtensions(): Promise<BaseExtension[]> {
        return this.extensions
            .map((extensionName) => ExtensionMap?.[extensionName]?.())
            .filter(Boolean);
    }

    async configureController() {
        const extensions = await this.constructExtensions();
        const baseConfig = new BaseEditorConfig({
            menuBar: !this.hideMenuBar,
            placeholder: this.placeholder || undefined,
        });
        const config = configureEditor({
            baseConfig,
            extensions,
        });
        this.pm = new ProseMirrorViewController(this, config, this.readingMode);
        this.additionalStyles = config.css;

        // Inject the view into extensions once it's available
        if (this.pm._view) {
            extensions.forEach((extension) =>
                extension.setView(this.pm!._view!)
            );
        }

        this.registerEditor();
    }

    update(changedProperties: Map<string, unknown>) {
        if (changedProperties.has("extensionNodes")) {
            this.configureController();
        }
        if (changedProperties.has("readingMode")) {
            this.pm?.setReadingMode(this.readingMode);
        }
        super.update(changedProperties);
    }

    render() {
        const dynamicStyles = this.additionalStyles.map((style) => {
            return html`<style>
                ${style.cssText}
            </style>`;
        });

        return html`
            ${dynamicStyles}
            <div
                class="main ${
                    this.darkMode ? "dark-mode sl-theme-dark" : "sl-theme-light"
                }"
                part="main"
            >
                ${this.pm?.viewNode}
            </div>
        `;
    }

    // Emit event to allow parent to load and get documents
    registerEditor() {
        this.dispatchEvent(
            new CustomEvent("c-text-editor:register", {
                bubbles: true,
                composed: true,
                detail: {
                    value: {
                        load: (source: string) => {
                            if (!this.pm) {
                                throw Error(
                                    "Cannot load document before ProseMirror controller is initialized."
                                );
                            }
                            this.pm.load(source);
                        },
                        getMarkdown: () => this.pm?.serialize?.() ?? "",
                    },
                },
            })
        );
    }

    static styles: CSSResultGroup[] = [
        unsafeCSS(shoelaceLight),
        unsafeCSS(shoelaceDark),
        unsafeCSS(shoelaceStekzTheme),
        unsafeCSS(defaultStyles),
        unsafeCSS(overridesStyles),
    ];
}

declare global {
    interface HTMLElementTagNameMap {
        "c-text-editor": TextEditor;
    }
}
